interface WidgetConfiguration {
  headerMessage: string,
  theme: string,
  widgetId: string;
  endpointUrl: string;
  region: string;
  stage: string;
  cell: string;
  parentUrl: string;
  publicUrl?: string;
  shouldShowMessageReceipts: string;
  staticStringsConfig?: string;
  displayNameConfig?: string;
  logoConfig?: string;
  isVoiceEnabled?: boolean,
  agentVideoOption?: string,
  customerVideoOption?: string
}
// Regex to validate Proxy domain pattern here: https://code.amazon.com/packages/LilyChatWidgetCommonConfigs/blobs/mainline/--/src/configs/chatWidgetCustomUrl.ts
// along with api location defined in proxy layer. Ref: https://code.amazon.com/packages/LilyWebsiteReverseProxy/blobs/mainline/--/configuration/etc/nginx/chat_widget.conf
const PROXY_ENDPOINT_URL_PATTERNS = {
  NONPROD: /^https:\/\/([\w-]+)\.my\.(dev|gamma)\.[\w-]+\.nonprod\.connect\.aws\.a2z.com\/connectwidget\/api$/,
  PROD: /^https:\/\/([\w-]+)\.my\.connect\.aws\/connectwidget\/api$/,
} as const;

declare global {
  var AMAZON_CONNECT_WIDGET_CONFIGURATION: WidgetConfiguration;
  var connect : any;
}

interface LogoConfig {
  sourceUrl: string;
  altText?: string;
} 

interface DisplayNameConfig {
  botMessage: string;
  systemMessage: string;
}

interface StaticStringsConfig {
  endChatButton: string;
  textInputPlaceholder: string;
}

interface CustomDisplayNames {
  header?: {
    headerMessage?: string;
    logoUrl?: string;
    logoAltText?: string;
  };
  transcript?: {
    botMessageDisplayName?: string;
    systemMessageDisplayName?: string;
  };
  footer?: {
    endChatButtonText?: string;
    textInputPlaceholder?: string;
  };
}

interface ChatConfigs {
  theme: string;
  displayNameConfig: DisplayNameConfig;
  staticStringsConfig: StaticStringsConfig;
  logoConfig: LogoConfig;
  headerText: string;
}

function parseBrandingConfig(config){
  if (!config) return {};
  let stringifiedJson = decodeURIComponent(config);
  return JSON.parse(stringifiedJson);
};

function initializeChatConfigurations(
  widgetConfiguration: WidgetConfiguration,
  chatProps: Record<string, string>
): ChatConfigs {

  const decodeHeaderText = (headerText: string): string => {
    try {
      return decodeURIComponent(headerText);
    } catch (Err) {
      console.error("Error while decoding using decodeURIComponent: trying unescape", Err);
      try {
        return unescape(headerText);
      } catch (e) {
        console.error("Error while decoding: unescape attribute was removed", e);
        return headerText;
      }
    }
  };

  // Initialize base chat configurations
  const configs: ChatConfigs = {
    theme: widgetConfiguration.theme,
    displayNameConfig: parseBrandingConfig(widgetConfiguration.displayNameConfig),
    staticStringsConfig: parseBrandingConfig(widgetConfiguration.staticStringsConfig),
    logoConfig: parseBrandingConfig(widgetConfiguration.logoConfig),
    headerText: decodeHeaderText(widgetConfiguration.headerMessage)
  };

  // Update configurations with custom display names if present
  if (chatProps['customDisplayNames']) {
    try {
      const customDisplayNameObj: CustomDisplayNames = JSON.parse(chatProps['customDisplayNames']);

      // Update header configurations
      if (customDisplayNameObj?.header) {
        configs.headerText = customDisplayNameObj.header.headerMessage ?? configs.headerText;
        configs.logoConfig.sourceUrl = customDisplayNameObj.header.logoUrl ?? configs.logoConfig.sourceUrl;
        configs.logoConfig.altText = customDisplayNameObj.header.logoAltText;
      }

      // Update transcript configurations
      if (customDisplayNameObj?.transcript) {
        configs.displayNameConfig.botMessage = customDisplayNameObj.transcript.botMessageDisplayName ?? configs.displayNameConfig.botMessage;
        configs.displayNameConfig.systemMessage = customDisplayNameObj.transcript.systemMessageDisplayName ?? configs.displayNameConfig.systemMessage;
      }

      // Update footer configurations
      if (customDisplayNameObj?.footer) {
        configs.staticStringsConfig.endChatButton = customDisplayNameObj.footer.endChatButtonText ?? configs.staticStringsConfig.endChatButton;
        configs.staticStringsConfig.textInputPlaceholder = customDisplayNameObj.footer.textInputPlaceholder ?? configs.staticStringsConfig.textInputPlaceholder;
      }
    } catch (error) {
      console.error('Error parsing custom display names:', error);
    }
  }

  return configs;
}


// Need this for Typescript
export {};

// If its not in an Iframe, we want to return blank HTML
if(window.self === window.top) {
  document.body.innerHTML = '';
} else {
  let passthroughHeaders = {};
  window.self.addEventListener('message', function(event : MessageEvent) {
    let origin = AMAZON_CONNECT_WIDGET_CONFIGURATION.parentUrl;

    let chatProps = {
      name: '',
      supportedMessagingContentTypes: '',
    };

    if (typeof event.data === 'object' && event.data.call === 'custom_start_chat_rendering') {
      const passthroughBody: Map<string, string> = event.data.body;
      //pass through body
      if (passthroughBody) {
        passthroughBody.forEach((value, key) => {
          if (value) {
            chatProps[key] = value;
          }
        });
      }
      const configs = initializeChatConfigurations(AMAZON_CONNECT_WIDGET_CONFIGURATION, chatProps);
      connect.ChatInterface.init({
        containerId: 'root',
        themeConfig: {
          ...JSON.parse(window.atob(configs.theme)),
        },
        displayNameConfig: configs.displayNameConfig,
        staticStringsConfig: configs.staticStringsConfig,
        logoConfig: configs.logoConfig,
        headerConfig: {
          headerText: configs.headerText,
        },
        widgetType: 'HostedChatWidget',
        shouldShowMessageReceipts: AMAZON_CONNECT_WIDGET_CONFIGURATION.shouldShowMessageReceipts,
        isVoiceEnabled: AMAZON_CONNECT_WIDGET_CONFIGURATION.isVoiceEnabled,
      });
    }

    if (typeof event.data === 'object' && event.data.call === 'amazon-connect-webform-started') {
      passthroughHeaders = event.data.data;
      const passthroughBody: Map<string, string> = event.data.body;
      if (connect.WebFormInterface && typeof connect.WebFormInterface.init === 'function') {
        let webformProps = {
          containerId: 'root',
          widgetConfiguration: AMAZON_CONNECT_WIDGET_CONFIGURATION,
          headers: passthroughHeaders,
          name: passthroughBody?.get('name') || '',
          displayType: passthroughBody?.get('displayType') || '',
          viewConfig: passthroughBody?.get('viewConfig') || '',
          theme: passthroughBody?.get('themeConfig') || ''
        };
        
        if (passthroughBody.get('language')) {
          webformProps['language'] = passthroughBody.get('language');
        }
  
        connect.WebFormInterface.init(webformProps);
      }
    } 

    if (typeof event.data === 'object' && event.data.call === 'amazon-connect-start-voice') {
      // When we receive a message from the initializer, send the VoiceInterface app the configuration
      let voiceProps = {
        name: '',
      };
      passthroughHeaders = event.data.data;
      const passthroughBody: Map<string, string> = event.data.body;
      voiceProps.name = passthroughBody?.get('name');

      if (passthroughBody.get('relatedChatContactId')) {
        voiceProps['relatedChatContactId'] = passthroughBody.get('relatedChatContactId');
      }

      if (passthroughBody.get('name')) {
        voiceProps['name'] = passthroughBody.get('name');
      }

      if (passthroughBody.get('language')) {
        voiceProps['language'] = passthroughBody.get('language');
      }

      if (passthroughBody.get('contactAttributes')) {
        voiceProps['contactAttributes'] = JSON.parse(passthroughBody.get('contactAttributes'));
      }

      if (connect.VoiceInterface && typeof connect.VoiceInterface.init === 'function') {
        connect.VoiceInterface.init({
          containerId: 'root',
          widgetConfiguration: AMAZON_CONNECT_WIDGET_CONFIGURATION,
          headers: passthroughHeaders,
          ...voiceProps
        });
      }
    } 

    if (typeof event.data === 'object' && event.data.call === 'amazon-connect-start-chat') {
      passthroughHeaders = event.data.data;
      const passthroughBody: Map<string, string> = event.data.body;
      //pass through body
      if (passthroughBody) {
        passthroughBody.forEach((value, key) => {
          if (value) {
            chatProps[key] = value;
          }
        });
      }

      if (connect.ChatInterface && typeof connect.ChatInterface.init === 'function') {
        // This check against multiple initializations of chat interface, since handleCallbacks calls this initialization at custom_start_chat_rendering callback as well
        if (!event.data.customStartChatAdded){
          const configs = initializeChatConfigurations(AMAZON_CONNECT_WIDGET_CONFIGURATION, chatProps);
          connect.ChatInterface.init({
            containerId: 'root',
            themeConfig: {
              ...JSON.parse(window.atob(configs.theme)),
            },
            displayNameConfig: configs.displayNameConfig,
            staticStringsConfig: configs.staticStringsConfig,
            logoConfig: configs.logoConfig,
            headerConfig: {
              headerText: configs.headerText,
            },
            widgetType: 'HostedChatWidget',
            shouldShowMessageReceipts: AMAZON_CONNECT_WIDGET_CONFIGURATION.shouldShowMessageReceipts,
            isVoiceEnabled: AMAZON_CONNECT_WIDGET_CONFIGURATION.isVoiceEnabled,
          });
        }
      }      
      // Prefix for proxy url already added in proxy layer. 
      // Ref: https://code.amazon.com/packages/LilyWebsiteReverseProxy/blobs/mainline/--/configuration/etc/nginx/chat_widget.conf
      const prefix = isConnectReverseProxyUrl(AMAZON_CONNECT_WIDGET_CONFIGURATION.endpointUrl) ? '' : '/widget';
      connect.ChatInterface.initiateChat(
        {
          // sample proxy APIGW URL for startChat API in prod : https://your-instance-alias.my.connect.aws/connectwidget/api/{widgetId}/start
          apiGatewayEndpoint: `${AMAZON_CONNECT_WIDGET_CONFIGURATION.endpointUrl}${prefix}/${AMAZON_CONNECT_WIDGET_CONFIGURATION.widgetId}/start`,
          headers: passthroughHeaders,
          region: AMAZON_CONNECT_WIDGET_CONFIGURATION.region,
          stage: AMAZON_CONNECT_WIDGET_CONFIGURATION.stage,
          cell: AMAZON_CONNECT_WIDGET_CONFIGURATION.cell,
          ...chatProps,
        },
        successHandler,
        failureHandler
      );

      if (connect.ChatEvents && typeof connect.ChatEvents.onVoiceEscalation === 'function') {
        connect.ChatEvents.onVoiceEscalation(({ chatContactId, authenticatedParticipantDisplayName }) => {
          const source = event.source as Window;
          source.postMessage({ call: 'amazon-connect-escalate-to-voice', chatContactId, authenticatedParticipantDisplayName }, origin);
        });
      }

      connect.ChatEvents.onStartNewChat((callback) => {
        const source = event.source as Window;
        source.postMessage({ call: 'amazon-connect-start-new-chat' }, origin);
      });

      connect.ChatEvents.onChatEnded((callback) => {
        const source = event.source as Window;
        source.postMessage({ call: 'amazon-connect-end-chat' }, origin);
      });
      connect.ChatEvents.onChatLoading((callback) => {
        const source = event.source as Window;
        source.postMessage({ call: 'amazon-connect-loading-chat' }, origin);
      });
      connect.ChatEvents.onPushNotificationEligibleMessageReceived((callback) => {
        const source = event.source as Window;
        source.postMessage({ call: 'amazon-connect-push-notification-eligible-message-received' }, origin);
      });
      connect.ChatEvents.onAgentEndChat((callback) => {
        const source = event.source as Window;
        source.postMessage({ call: 'amazon-connect-agent-end-chat' }, origin);
      });
      function successHandler(session) {
        const source = event.source as Window;
        source.postMessage(
          { call: 'amazon-connect-chat-started', persistedChatSession: session.persistedChatSession, contactId: session.contactId },
          origin
        );
      }
      function failureHandler() {
        const source = event.source as Window;
        source.postMessage({ call: 'amazon-connect-chat-error' }, origin);
        console.error('Chat widget failed to initialize');
      }
      function isConnectReverseProxyUrl(endpointUrl: string): boolean {
        if (!endpointUrl) return false;
        
        return Object.keys(PROXY_ENDPOINT_URL_PATTERNS).some(key => 
          PROXY_ENDPOINT_URL_PATTERNS[key as keyof typeof PROXY_ENDPOINT_URL_PATTERNS].test(endpointUrl)
        );
      }
    }
  }, false);
}
